import React, { createContext, useState, useContext, useEffect } from 'react';
import { cartService } from '../services';
import { useAuth } from './AuthContext';

const CartContext = createContext(null);

const GUEST_CART_KEY = 'electronics_shop_guest_cart';

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [], total: 0, itemCount: 0 });
  const [loading, setLoading] = useState(false);
  const { isAuthenticated } = useAuth();

  // Load guest cart from localStorage
  const loadGuestCart = () => {
    try {
      const savedCart = localStorage.getItem(GUEST_CART_KEY);
      if (savedCart) {
        const parsed = JSON.parse(savedCart);
        return parsed;
      }
    } catch (error) {
      console.error('Error loading guest cart:', error);
    }
    return { items: [], total: 0, itemCount: 0 };
  };

  // Save guest cart to localStorage
  const saveGuestCart = (cartData) => {
    try {
      localStorage.setItem(GUEST_CART_KEY, JSON.stringify(cartData));
    } catch (error) {
      console.error('Error saving guest cart:', error);
    }
  };

  // Calculate cart totals
  const calculateCart = (items) => {
    const total = items.reduce((sum, item) => sum + (item.price * item.quantity), 0);
    const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);
    return { items, total, itemCount };
  };

  const fetchCart = async () => {
    if (!isAuthenticated) {
      // Load from localStorage for guest users
      const guestCart = loadGuestCart();
      setCart(guestCart);
      return;
    }

    try {
      setLoading(true);
      const response = await cartService.get();
      const serverCart = response.data;
      
      // Sync guest cart to server if user just logged in
      const guestCart = loadGuestCart();
      if (guestCart.items.length > 0) {
        // Merge guest cart with server cart
        for (const guestItem of guestCart.items) {
          try {
            await cartService.add({ 
              product_id: guestItem.product_id, 
              quantity: guestItem.quantity 
            });
          } catch (error) {
            console.error('Error syncing guest cart item:', error);
          }
        }
        // Clear guest cart after syncing
        localStorage.removeItem(GUEST_CART_KEY);
        // Refetch to get updated cart
        const updatedResponse = await cartService.get();
        setCart(updatedResponse.data);
      } else {
        setCart(serverCart);
      }
    } catch (error) {
      console.error('Error fetching cart:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const addToCart = async (productId, quantity = 1, productDetails = null) => {
    if (!isAuthenticated) {
      // Guest user - manage cart in localStorage
      const guestCart = loadGuestCart();
      const existingItemIndex = guestCart.items.findIndex(item => item.product_id === productId);
      
      if (existingItemIndex > -1) {
        guestCart.items[existingItemIndex].quantity += quantity;
      } else {
        // Need product details for guest cart
        if (!productDetails) {
          throw new Error('Product details required for guest cart');
        }
        guestCart.items.push({
          id: Date.now(), // Temporary ID for guest cart
          product_id: productId,
          quantity,
          name: productDetails.name,
          price: productDetails.price,
          image: productDetails.image,
          sku: productDetails.sku
        });
      }
      
      const updatedCart = calculateCart(guestCart.items);
      saveGuestCart(updatedCart);
      setCart(updatedCart);
      return true;
    }

    try {
      await cartService.add({ product_id: productId, quantity });
      await fetchCart();
      return true;
    } catch (error) {
      console.error('Error adding to cart:', error);
      throw error;
    }
  };

  const updateCartItem = async (itemId, quantity) => {
    if (!isAuthenticated) {
      // Guest user
      const guestCart = loadGuestCart();
      const itemIndex = guestCart.items.findIndex(item => item.id === itemId);
      if (itemIndex > -1) {
        guestCart.items[itemIndex].quantity = quantity;
        const updatedCart = calculateCart(guestCart.items);
        saveGuestCart(updatedCart);
        setCart(updatedCart);
      }
      return;
    }

    try {
      await cartService.update(itemId, { quantity });
      await fetchCart();
    } catch (error) {
      console.error('Error updating cart:', error);
      throw error;
    }
  };

  const removeFromCart = async (itemId) => {
    if (!isAuthenticated) {
      // Guest user
      const guestCart = loadGuestCart();
      guestCart.items = guestCart.items.filter(item => item.id !== itemId);
      const updatedCart = calculateCart(guestCart.items);
      saveGuestCart(updatedCart);
      setCart(updatedCart);
      return;
    }

    try {
      await cartService.remove(itemId);
      await fetchCart();
    } catch (error) {
      console.error('Error removing from cart:', error);
      throw error;
    }
  };

  const clearCart = async () => {
    if (!isAuthenticated) {
      // Guest user
      localStorage.removeItem(GUEST_CART_KEY);
      setCart({ items: [], total: 0, itemCount: 0 });
      return;
    }

    try {
      await cartService.clear();
      setCart({ items: [], total: 0, itemCount: 0 });
    } catch (error) {
      console.error('Error clearing cart:', error);
      throw error;
    }
  };

  return (
    <CartContext.Provider value={{
      cart,
      loading,
      fetchCart,
      addToCart,
      updateCartItem,
      removeFromCart,
      clearCart
    }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};
